import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ipdBillingApi } from '@/api/ipdBilling';
import type { GSTMaster, IPDChargeSourceType } from '@/types/ipdBilling';

const SOURCE_TYPES: IPDChargeSourceType[] = ['ROOM', 'VISIT', 'SURGERY', 'LAB', 'PHARMA', 'CONSUMABLE', 'MISC'];

export default function GSTManagement() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editGSTId, setEditGSTId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    gstCode: '',
    rate: '',
    applicableSourceTypes: '',
    hsnSac: '',
    hospitalId: isSuperAdmin ? '' : user?.hospitalId ?? '',
    thresholdAmount: '',
    applyToIncremental: false,
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: '',
  });
  const queryClient = useQueryClient();

  const { data: gstMastersRes, isLoading } = useQuery({
    queryKey: ['gst-masters', isSuperAdmin ? 'all' : user?.hospitalId],
    queryFn: async () => {
      const response = await ipdBillingApi.listGSTMasters(isSuperAdmin ? undefined : user?.hospitalId);
      return response.data?.data ?? [];
    },
  });
  const gstMasters = gstMastersRes ?? [];

  const addMutation = useMutation({
    mutationFn: ipdBillingApi.createGSTMaster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gst-masters'] });
      setIsAddDialogOpen(false);
      setFormData({
        gstCode: '',
        rate: '',
        applicableSourceTypes: '',
        hsnSac: '',
        hospitalId: isSuperAdmin ? '' : user?.hospitalId ?? '',
        thresholdAmount: '',
        applyToIncremental: false,
        effectiveFrom: new Date().toISOString().slice(0, 10),
        effectiveTo: '',
      });
      toast.success('GST Master created successfully');
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'Failed to create GST Master');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      ipdBillingApi.updateGSTMaster(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gst-masters'] });
      setIsEditDialogOpen(false);
      setEditGSTId(null);
      toast.success('GST Master updated successfully');
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'Failed to update GST Master');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ipdBillingApi.deleteGSTMaster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gst-masters'] });
      toast.success('GST Master deleted successfully');
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'Failed to delete GST Master');
    },
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.gstCode || !formData.rate || !formData.applicableSourceTypes) {
      toast.error('Please fill all required fields');
      return;
    }
    const rate = parseFloat(formData.rate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('GST rate must be between 0 and 100');
      return;
    }
    addMutation.mutate({
      gstCode: formData.gstCode,
      rate,
      applicableSourceTypes: formData.applicableSourceTypes,
      hsnSac: formData.hsnSac || undefined,
      hospitalId: isSuperAdmin ? (formData.hospitalId || null) : (user?.hospitalId ?? null),
      thresholdAmount: formData.thresholdAmount ? parseFloat(formData.thresholdAmount) : null,
      applyToIncremental: formData.applyToIncremental,
      effectiveFrom: formData.effectiveFrom || undefined,
      effectiveTo: formData.effectiveTo || undefined,
    });
  };

  const handleEditClick = (gst: GSTMaster) => {
    setEditGSTId(gst.id);
    setFormData({
      gstCode: gst.gstCode,
      rate: gst.rate.toString(),
      applicableSourceTypes: gst.applicableSourceTypes,
      hsnSac: gst.hsnSac || '',
      hospitalId: gst.hospitalId || '',
      thresholdAmount: gst.thresholdAmount?.toString() || '',
      applyToIncremental: gst.applyToIncremental ?? false,
      effectiveFrom: gst.effectiveFrom.slice(0, 10),
      effectiveTo: gst.effectiveTo?.slice(0, 10) || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editGSTId) return;
    const rate = parseFloat(formData.rate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('GST rate must be between 0 and 100');
      return;
    }
    updateMutation.mutate({
      id: editGSTId,
      data: {
        gstCode: formData.gstCode,
        rate,
        applicableSourceTypes: formData.applicableSourceTypes,
        hsnSac: formData.hsnSac || undefined,
        thresholdAmount: formData.thresholdAmount ? parseFloat(formData.thresholdAmount) : null,
        applyToIncremental: formData.applyToIncremental,
        effectiveFrom: formData.effectiveFrom || undefined,
        effectiveTo: formData.effectiveTo || undefined,
      },
    });
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this GST Master?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="thms-page-shell">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">GST Master Management</h1>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add GST Master
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>GST Code</TableHead>
                <TableHead>Rate (%)</TableHead>
                <TableHead>Applicable To</TableHead>
                <TableHead>Threshold</TableHead>
                <TableHead>Incremental</TableHead>
                <TableHead>HSN/SAC</TableHead>
                {isSuperAdmin && <TableHead>Hospital</TableHead>}
                <TableHead>Effective From</TableHead>
                <TableHead>Effective To</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gstMasters && gstMasters.length > 0 ? (
                gstMasters.map((gst) => (
                  <TableRow key={gst.id}>
                    <TableCell className="font-medium">{gst.gstCode}</TableCell>
                    <TableCell>{gst.rate}%</TableCell>
                    <TableCell className="text-sm">{gst.applicableSourceTypes}</TableCell>
                    <TableCell>{gst.thresholdAmount ? `₹${gst.thresholdAmount.toLocaleString()}` : '—'}</TableCell>
                    <TableCell>{gst.applyToIncremental ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{gst.hsnSac || '—'}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>{gst.hospital?.name || 'Global'}</TableCell>
                    )}
                    <TableCell>{new Date(gst.effectiveFrom).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {gst.effectiveTo ? new Date(gst.effectiveTo).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditClick(gst)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(gst.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 10 : 9} className="text-center text-muted-foreground">
                    No GST Masters found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add GST Master</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div>
              <Label htmlFor="gstCode">GST Code *</Label>
              <Input
                id="gstCode"
                value={formData.gstCode}
                onChange={(e) => setFormData({ ...formData, gstCode: e.target.value })}
                placeholder="e.g. GST_18"
                required
              />
            </div>
            <div>
              <Label htmlFor="rate">GST Rate (%) *</Label>
              <Input
                id="rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                placeholder="e.g. 18"
                required
              />
            </div>
            <div>
              <Label htmlFor="applicableSourceTypes">Applicable Source Types *</Label>
              <Input
                id="applicableSourceTypes"
                value={formData.applicableSourceTypes}
                onChange={(e) => setFormData({ ...formData, applicableSourceTypes: e.target.value })}
                placeholder="e.g. ROOM,VISIT,SURGERY,LAB (comma-separated)"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Available: {SOURCE_TYPES.join(', ')}
              </p>
            </div>
            <div>
              <Label htmlFor="hsnSac">HSN/SAC Code</Label>
              <Input
                id="hsnSac"
                value={formData.hsnSac}
                onChange={(e) => setFormData({ ...formData, hsnSac: e.target.value })}
                placeholder="e.g. 9993"
              />
            </div>
            <div>
              <Label htmlFor="thresholdAmount">Threshold Amount (optional)</Label>
              <Input
                id="thresholdAmount"
                type="number"
                min="0"
                step="0.01"
                value={formData.thresholdAmount}
                onChange={(e) => setFormData({ ...formData, thresholdAmount: e.target.value })}
                placeholder="e.g. 5000"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.applicableSourceTypes.toUpperCase().includes('ROOM') 
                  ? 'For ROOM charges: Threshold is per day. GST applies only if daily room rate exceeds this threshold.'
                  : 'If set, GST will only apply when the charge amount exceeds this threshold'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="applyToIncremental"
                checked={formData.applyToIncremental}
                onChange={(e) => setFormData({ ...formData, applyToIncremental: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="applyToIncremental" className="cursor-pointer">
                Apply GST to incremental amount only (above threshold)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {formData.applicableSourceTypes.toUpperCase().includes('ROOM')
                ? 'For ROOM: GST applies to (daily rate - threshold) × number of days. Example: If room rent is ₹7000/day and threshold is ₹5000/day, GST applies to ₹2000/day. For 5 days stay, GST applies to ₹2000 × 5 = ₹10000.'
                : 'If checked and threshold is set, GST applies only to (amount - threshold). Example: If charge is ₹7000 and threshold is ₹5000, GST applies only to ₹2000.'}
            </p>
            {isSuperAdmin && (
              <div>
                <Label htmlFor="hospitalId">Hospital ID (leave empty for global)</Label>
                <Input
                  id="hospitalId"
                  value={formData.hospitalId}
                  onChange={(e) => setFormData({ ...formData, hospitalId: e.target.value })}
                  placeholder="Leave empty for global GST rate"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="effectiveFrom">Effective From</Label>
                <Input
                  id="effectiveFrom"
                  type="date"
                  value={formData.effectiveFrom}
                  onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="effectiveTo">Effective To (optional)</Label>
                <Input
                  id="effectiveTo"
                  type="date"
                  value={formData.effectiveTo}
                  onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit GST Master</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-gstCode">GST Code *</Label>
              <Input
                id="edit-gstCode"
                value={formData.gstCode}
                onChange={(e) => setFormData({ ...formData, gstCode: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-rate">GST Rate (%) *</Label>
              <Input
                id="edit-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-applicableSourceTypes">Applicable Source Types *</Label>
              <Input
                id="edit-applicableSourceTypes"
                value={formData.applicableSourceTypes}
                onChange={(e) => setFormData({ ...formData, applicableSourceTypes: e.target.value })}
                placeholder="e.g. ROOM,VISIT,SURGERY,LAB"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-hsnSac">HSN/SAC Code</Label>
              <Input
                id="edit-hsnSac"
                value={formData.hsnSac}
                onChange={(e) => setFormData({ ...formData, hsnSac: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-thresholdAmount">Threshold Amount (optional)</Label>
              <Input
                id="edit-thresholdAmount"
                type="number"
                min="0"
                step="0.01"
                value={formData.thresholdAmount}
                onChange={(e) => setFormData({ ...formData, thresholdAmount: e.target.value })}
                placeholder="e.g. 5000"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.applicableSourceTypes.toUpperCase().includes('ROOM') 
                  ? 'For ROOM charges: Threshold is per day. GST applies only if daily room rate exceeds this threshold.'
                  : 'If set, GST will only apply when the charge amount exceeds this threshold'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-applyToIncremental"
                checked={formData.applyToIncremental}
                onChange={(e) => setFormData({ ...formData, applyToIncremental: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="edit-applyToIncremental" className="cursor-pointer">
                Apply GST to incremental amount only (above threshold)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {formData.applicableSourceTypes.toUpperCase().includes('ROOM')
                ? 'For ROOM: GST applies to (daily rate - threshold) × number of days. Example: If room rent is ₹7000/day and threshold is ₹5000/day, GST applies to ₹2000/day. For 5 days stay, GST applies to ₹2000 × 5 = ₹10000.'
                : 'If checked and threshold is set, GST applies only to (amount - threshold). Example: If charge is ₹7000 and threshold is ₹5000, GST applies only to ₹2000.'}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-effectiveFrom">Effective From</Label>
                <Input
                  id="edit-effectiveFrom"
                  type="date"
                  value={formData.effectiveFrom}
                  onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-effectiveTo">Effective To (optional)</Label>
                <Input
                  id="edit-effectiveTo"
                  type="date"
                  value={formData.effectiveTo}
                  onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
